import React, { useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';

import Avatar from '@material-ui/core/Avatar';
import Chip from '@material-ui/core/Chip';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { userSelector } from '../../reducers/current-user-selectors';

import { UiButton } from '../../ui/ui-button';

import { FormattedMessageBody } from './formatted-message-body';
import { InlineContactFile } from './inline-contact-file';
import { LegacyTranslateMessageButton } from './legacy-translate-message-button';

import { HTMLStripper } from '../../util/html-stripper';
import { LanguageCode, LANGUAGES } from '../../util/languages';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { AnnouncementStyling } from '../utils/announcement-styling';
import { useSentMessageTranslation } from '../utils/use-sent-message-translation';

const styles = () =>
  createStyles({
    requiresResponse: {
      width: '20px',
      height: '20px',
      fontSize: '1rem',
    },
  });

const mapStateToProps = (state: any) => ({
  user: userSelector(state),
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type Props = ReduxProps &
  WithStyles<typeof styles> & {
    senderName: string;
    sendDateTime?: string | Date;
    timeZone?: string;
    messageBody: string;
    subject: string;

    announcement?: Announcement | null;
    className?: string;
    recipientName?: string;
    requiresResponse?: boolean | null;
    disableOtherLanguagesMessage?: boolean;
    isAnnouncement?: boolean;
    isInbound?: boolean;
    isAutomated?: boolean;
    isFailed?: boolean;
    forceAllowEditing?: boolean;
    allowLinkToAnnouncement?: boolean;
    urlVisitDetails?: { [pathLowerCased: string]: VisitDetail };

    otherLanguages?: Array<{
      languageCode: LanguageCode;
      messageBody: string;
      subject?: string;
    }>;
    maxCharacters?: number;

    onEditMessage?: () => void;
    onCancelMessage?: () => void;

    contactFilesWithUrls?: { contactFile: ContactFile; presignedUrl: string }[];
    messageIdAndLanguageCode?: MessageIdAndLanguageCodeType;

    dataCy?: string;
  };

export const EmailMessage = connect(mapStateToProps)(
  withStyles(styles)(
    ({
      classes,
      className,
      isInbound,
      isAutomated,
      messageBody,
      user,
      maxCharacters,
      senderName,
      recipientName,
      sendDateTime,
      timeZone,
      requiresResponse,
      subject,
      isAnnouncement,
      isFailed,
      otherLanguages,
      onEditMessage,
      onCancelMessage,
      disableOtherLanguagesMessage,
      announcement,
      allowLinkToAnnouncement,
      forceAllowEditing,
      urlVisitDetails,
      contactFilesWithUrls,
      dataCy,
      messageIdAndLanguageCode,
    }: Props) => {
      const {
        disableTranslateButton,
        disableTranslateButtonMessage,
        shouldShowTranslation,
        loading: translationLoading,
        translatedMessageBody,
        translatedSubject,
        onTranslateMessageClick,
        hideTranslation,
      } = useSentMessageTranslation({
        messageId: messageIdAndLanguageCode?.messageId,
        messageBody,
        subject,
      });

      isAnnouncement = isAnnouncement || !!announcement;
      const { announcementGroupLabel, backgroundClass } = AnnouncementStyling({
        announcement,
        allowLinkToAnnouncement,
        isAnnouncement,
        isInbound,
        isAutomated,
      });

      const [showFullMessage, setShowFullMessage] = useState<boolean>(false);
      // only used for outbound bulk messages sent in multiple languages
      const [displayLanguage, setDisplayLanguage] = useState<LanguageCode>('en');

      let classNameArray = ['u-wordBreak-breakWord', 'u-padding', 'u-margin-sm'];
      // If the message isn't HTML, we need the preWrap to preserve the new lines
      // in the email. Note that the two types of messages that are stored as HTML are
      // messages sent from the UI and messages sent from our automated texting infrastructure
      if (!isAutomated && !HTMLStripper.isHTML(messageBody)) {
        classNameArray = classNameArray.concat('u-whiteSpace-preWrap');
      }

      const handleShowFullMessageClick = () => setShowFullMessage(!showFullMessage);

      const onOtherLanguageClick = (languageCode: LanguageCode) => () => {
        setDisplayLanguage(languageCode);
      };

      const allLanguages = (otherLanguages || [])?.concat({
        languageCode: 'en',
        messageBody,
        subject,
      });
      const hiddenLanguages = allLanguages.filter(
        ({ languageCode }) => languageCode !== displayLanguage,
      );

      let messageBodyToUse;
      let subjectToUse;
      // outbound bulk message sent in multiple languages
      if (!isInbound && otherLanguages) {
        ({ messageBody: messageBodyToUse, subject: subjectToUse } = allLanguages.find(
          ({ languageCode }) => languageCode === displayLanguage,
        ) || { messageBody, subject });
      } else {
        messageBodyToUse =
          shouldShowTranslation && translatedMessageBody ? translatedMessageBody : messageBody;
        subjectToUse = shouldShowTranslation && translatedSubject ? translatedSubject : subject;
      }
      const didTruncateMessage =
        maxCharacters !== undefined && messageBodyToUse.length > maxCharacters;
      const truncatedMessage =
        didTruncateMessage && !showFullMessage
          ? `${messageBodyToUse.slice(0, maxCharacters)}<span>...</span>`
          : messageBodyToUse;
      const isEditAllowed =
        forceAllowEditing || !sendDateTime || moment(sendDateTime).isAfter(moment());

      return (
        <div
          className={classNames(
            className,
            'u-background-white u-border u-border-black u-margin u-borderRadius-5',
          )}
          data-cy="email-container"
        >
          <Typography
            variant="caption"
            className=" u-border-bottom u-border-black u-padding"
            component="div"
          >
            <div
              style={{
                flexWrap: 'wrap',
              }}
              className="u-display-flex u-justifyContent-spaceBetween"
            >
              <div>
                <Chip
                  size="small"
                  className={backgroundClass}
                  label={
                    <Typography variant="caption">
                      {`From: ${senderName}`}
                      {announcementGroupLabel()}
                    </Typography>
                  }
                />
                {recipientName && <div>To: {recipientName}</div>}
                <div>Subject: {subjectToUse}</div>
                {isFailed ? (
                  <div className="typo-subtitle u-color-red u-margin-top-sm u-flex-center">
                    <MaterialDesignIcon
                      iconName={ICON_NAMES.WARNING}
                      className="u-margin-right-sm"
                      size="sm"
                    />
                    <div>Message Failed to Send</div>
                  </div>
                ) : null}
              </div>
              <div>
                {moment(sendDateTime).format('dddd, MMMM Do [at] h:mma')} {timeZone}
              </div>
              {user.isAdmin && requiresResponse && (
                <Avatar variant="circle" className={classes.requiresResponse}>
                  R
                </Avatar>
              )}
            </div>
          </Typography>
          <div className="u-flexDirection-column">
            <FormattedMessageBody
              className={classNameArray.join(' ')}
              messageBody={truncatedMessage}
              includesHTML={HTMLStripper.isHTML(messageBody)}
              shouldTrim={false}
              urlVisitDetails={urlVisitDetails}
              dataCy={dataCy}
            />
            {messageIdAndLanguageCode?.messageId && (
              <LegacyTranslateMessageButton
                className="u-padding-md"
                isTranslated={shouldShowTranslation}
                loading={translationLoading}
                onTranslateMessageClick={onTranslateMessageClick}
                onShowOriginalClick={hideTranslation}
                languageCode={messageIdAndLanguageCode.messageLanguageCode}
                disabled={disableTranslateButton}
                disabledTooltip={disableTranslateButtonMessage}
              />
            )}
          </div>
          <div className="u-padding-x">
            {contactFilesWithUrls?.map(({ contactFile, presignedUrl }) => (
              <InlineContactFile
                key={`contact-file-${contactFile.contactFileId}`}
                contactFile={contactFile}
                presignedUrl={presignedUrl}
              />
            ))}
          </div>
          {isEditAllowed && (onEditMessage || onCancelMessage) ? (
            <div className={didTruncateMessage ? 'u-spread u-margin-left-md' : ''}>
              {didTruncateMessage ? (
                <div className="typo-link" role="presentation" onClick={handleShowFullMessageClick}>
                  {showFullMessage ? 'Show Less' : 'Show More'}
                </div>
              ) : null}
              <div className="u-display-flex u-justifyContent-flexEnd u-margin-bottom-md">
                {onEditMessage ? (
                  <UiButton
                    variant="tertiary"
                    size="xs"
                    onClick={onEditMessage}
                    className="u-margin-right-md"
                    dataCy="email-edit"
                  >
                    Edit
                  </UiButton>
                ) : null}
                {onCancelMessage ? (
                  <UiButton
                    variant="tertiary"
                    size="xs"
                    onClick={onCancelMessage}
                    className="u-margin-right-md"
                  >
                    Cancel
                  </UiButton>
                ) : null}
              </div>
            </div>
          ) : null}
          {/* NOTE: this UI does not extend nicely to the case where messages
              were sent in more than 2 languages. We should clean this up when
              supporting sending messages in more than 2 languages */}
          {!disableOtherLanguagesMessage &&
            otherLanguages &&
            hiddenLanguages.map(({ languageCode }) => (
              <div key={languageCode} className="typo-subtitle u-margin-left-md">
                <span
                  role="presentation"
                  className="typo-link"
                  onClick={onOtherLanguageClick(languageCode)}
                  data-cy="announcement-translate"
                >
                  View {LANGUAGES[languageCode]} version
                </span>
              </div>
            ))}
        </div>
      );
    },
  ),
);
